"use client";

import { useEffect, useRef, useState } from "react";
import { locations } from "@/lib/constants";
import { Location } from "@/types/constants_type";
import BusinessHours from "./BusinessHours";
import ContactForm from "./ContactForm";

interface CountryContactPageProps {
  /** Headquarters code ("sg" | "th" | "vn" | "global"). */
  headquarters: string;
  /** Fallback index if no location matches. */
  fallbackIndex?: number;
  /** When true, hides the right-side form (used for the Global page). */
  hideForm?: boolean;
}

/**
 * Wrapper for country-specific Contact pages.
 *
 * Owns the animated decorative background + two-column layout
 * (Business Hours on the left, Contact Form on the right).
 */
export default function CountryContactPage({
  headquarters,
  fallbackIndex = 3,
  hideForm = false,
}: CountryContactPageProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    section.addEventListener("mousemove", handleMouseMove);
    return () => section.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const location: Location =
    locations.find((loc) => loc.headquarters === headquarters) || locations[fallbackIndex];

  return (
    <section
      ref={sectionRef}
      /* `overflow-hidden` is what keeps the cursor glow — a 300px blurred circle
         that follows the pointer — inside the section, so it has to stay, and
         that makes this section's own top edge a clip line. The form card's
         fingerprint hangs 71px above the card, so it is only at fault from lg:,
         where the grid puts the card in the top row: 71px of it then sits above
         the section and gets sliced flat. Below lg: the card is the second row
         and the fingerprint hangs into the 100px column gap, which is already
         clear, so the tight padding stays there. 76px is the overhang plus 5.
         Padding here rather than a margin on the card, so the two columns keep
         the top alignment they are laid out with; and the Global page carries
         no card, so nothing overhangs and it keeps the tight one throughout. */
      className={`relative w-full min-h-max ${
        hideForm ? "pt-2" : "pt-2 lg:pt-[76px]"
      } pb-6 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden`}
    >
      {/* Interactive cursor glow */}
      <div
        className="pointer-events-none absolute w-[300px] h-[300px] rounded-full opacity-0 hover:opacity-20 transition-opacity duration-300 z-[5]"
        style={{
          left: mousePos.x - 150,
          top: mousePos.y - 150,
          background: "radial-gradient(circle, rgba(236, 25, 56, 0.3) 0%, transparent 70%)",
          filter: "blur(40px)",
          transition: "left 0.1s ease-out, top 0.1s ease-out",
        }}
      />

      {/* Radial gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(236, 25, 56, 0.05) 0%, transparent 50%)",
        }}
      />

      <div className="max-w-[1300px] w-full mx-auto relative z-10">
        <div
          className={
            hideForm
              ? "grid grid-cols-1 gap-[100px] lg:gap-[160px] items-start"
              : "grid grid-cols-1 lg:grid-cols-2 gap-[100px] lg:gap-[160px] items-start"
          }
        >
          <BusinessHours location={location} />
          {!hideForm && <ContactForm location={location} />}
        </div>
      </div>
    </section>
  );
}
